/*
 * ManageSeatsForm.tsx (AbstractECommerce)
 *
 * Copyright © 2026 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rogiel Sulzbach, 2026
 *
 * @file ManageSeatsForm.tsx
 * @author Rogiel Sulzbach
 * @copyright 2026 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { InputText } from 'primereact/inputtext';
import DialogWrapper from '@abstract/abstractwebcommon-client/DialogWrapper/DialogWrapper';
import { currencyFormatter } from '@abstract/abstractwebcommon-shared/utils/currencyFormatter';
import {
  ISubscription,
  SeatChangeDirection
} from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/Subscription';
import {
  getMinSeatQuantity,
  validateSeatQuantity
} from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/Product';
import {
  changeSeatCountAction,
  previewSeatChangeAction,
  resetSeatChangePreview
} from '../../Store/Subscriptions/SharedSubscriptions';

/**
 * @interface IManageSeatsFormProperties
 */
interface IManageSeatsFormProperties {
  subscription: ISubscription /**< The seat-based subscription whose seats are being managed. */;
  isVisible: boolean /**< Controls dialog visibility. */;
  onHide: () => void /**< Close the dialog. */;
  subscriptionsList: ISubscription[] /**< Current cached subscription list, patched on success. */;
  isAdminCall: boolean /**< True when rendered from the admin table. */;
}

/**
 * A self-contained dialog that lets an admin or the customer change the number of billed seats on a
 * seat-based subscription. It shows a live, server-computed preview of the pro-rated charge or
 * refund before the change is applied.
 */
const ManageSeatsForm = (
  properties: IManageSeatsFormProperties
): JSX.Element => {
  const translation = useTranslation().t;
  const dispatch = useDispatch();

  const {
    subscription,
    isVisible,
    onHide,
    subscriptionsList,
    isAdminCall
  } = properties;

  const product = subscription.product;
  const minSeats: number = getMinSeatQuantity(product ?? {});
  const maxSeats: number | null =
    product && product.maxSeatQuantity ? Number(product.maxSeatQuantity) : null;

  const currentSeatCount: number =
    subscription.seatCount && subscription.seatCount > 0
      ? subscription.seatCount
      : minSeats;

  const [seatCount, setSeatCount] = useState<number>(currentSeatCount);

  const {
    seatChangePreview,
    isPreviewingSeatChange,
    isChangingSeatCount
  } = useSelector((state: any) => state.sharedSubscriptions);

  // Reset the local seat count whenever the dialog is (re)opened for a subscription.
  useEffect(() => {
    if (isVisible) {
      setSeatCount(currentSeatCount);
    }
  }, [isVisible, subscription._id]);

  // Fetch a fresh pro-ration preview whenever the requested seat count changes to a valid, changed
  // value. Debounced so typing does not spam the endpoint.
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const isValid =
      validateSeatQuantity(product ?? {}, seatCount) === null &&
      seatCount !== currentSeatCount;

    if (!isValid) {
      dispatch(resetSeatChangePreview());
      return;
    }

    const timer = setTimeout(() => {
      dispatch(previewSeatChangeAction(subscription._id as string, seatCount));
    }, 500);

    return () => clearTimeout(timer);
  }, [seatCount, isVisible]);

  const clampSeatCount = (value: number): number => {
    if (!Number.isFinite(value)) {
      return minSeats;
    }
    let next = Math.floor(value);
    if (next < minSeats) {
      next = minSeats;
    }
    if (maxSeats != null && next > maxSeats) {
      next = maxSeats;
    }
    return next;
  };

  const validationReason = validateSeatQuantity(product ?? {}, seatCount);
  const isUnchanged: boolean = seatCount === currentSeatCount;
  const isConfirmDisabled: boolean =
    validationReason !== null ||
    isUnchanged ||
    isChangingSeatCount ||
    isPreviewingSeatChange;

  const handleHide = (): void => {
    dispatch(resetSeatChangePreview());
    onHide();
  };

  const handleConfirm = (): void => {
    dispatch(
      changeSeatCountAction(
        subscription._id as string,
        seatCount,
        subscriptionsList,
        isAdminCall
      )
    );
    handleHide();
  };

  const preview =
    seatChangePreview && Number(seatChangePreview.newSeatCount) === seatCount
      ? seatChangePreview
      : null;

  const isUpgrade: boolean =
    preview?.direction === SeatChangeDirection.Upgrade;

  const renderFooter = (): JSX.Element => (
    <div className="d-flex align-items-center justify-content-between">
      <Button
        variant="secondary"
        className="d-flex align-items-center"
        onClick={handleHide}
      >
        <i className="bi bi-x-circle btn-icon"></i>
        {translation('manageSeats.cancel_button', { defaultValue: 'Cancel' })}
      </Button>
      <Button
        variant="primary"
        className="d-flex align-items-center m-0"
        disabled={isConfirmDisabled}
        onClick={handleConfirm}
      >
        <i className="bi bi-check2-circle btn-icon"></i>
        {translation('manageSeats.confirm_button', {
          defaultValue: 'Confirm change'
        })}
      </Button>
    </div>
  );

  return (
    <DialogWrapper
      isDialogVisible={isVisible}
      onHide={handleHide}
      headerTitle={translation('manageSeats.header', {
        defaultValue: 'Manage seats'
      })}
      footer={renderFooter()}
      isCustomClassName
    >
      <Col className="px-0">
        <p className="mb-2">
          {translation('manageSeats.description', {
            defaultValue:
              'Change the number of seats for this subscription. A pro-rated charge or refund is applied immediately for the remainder of the current billing period, and the new price applies from your next renewal.'
          })}
        </p>

        <Row className="align-items-center mb-3">
          <Col xs="auto" className="pr-2">
            <span className="font-weight-bold">
              {translation('manageSeats.seats_label', {
                defaultValue: 'Seats'
              })}
              :
            </span>
          </Col>
          <Col xs="auto" className="d-flex align-items-center">
            <Button
              variant="outline-secondary"
              className="d-flex align-items-center"
              disabled={seatCount <= minSeats}
              onClick={() => setSeatCount(clampSeatCount(seatCount - 1))}
              aria-label={translation('manageSeats.decrease', {
                defaultValue: 'Decrease seats'
              })}
            >
              <i className="pi pi-minus"></i>
            </Button>
            <InputText
              id="seatCount"
              className="mx-2 text-center"
              style={{ width: '5rem' }}
              value={String(seatCount)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const parsed = parseInt(event.target.value, 10);
                setSeatCount(Number.isNaN(parsed) ? minSeats : parsed);
              }}
              onBlur={() => setSeatCount(clampSeatCount(seatCount))}
            />
            <Button
              variant="outline-secondary"
              className="d-flex align-items-center"
              disabled={maxSeats != null && seatCount >= maxSeats}
              onClick={() => setSeatCount(clampSeatCount(seatCount + 1))}
              aria-label={translation('manageSeats.increase', {
                defaultValue: 'Increase seats'
              })}
            >
              <i className="pi pi-plus"></i>
            </Button>
          </Col>
        </Row>

        <p className="mb-1 text-muted">
          {translation('manageSeats.current_seats', {
            defaultValue: 'Current seats'
          })}
          : {currentSeatCount}
          {maxSeats != null
            ? ` — ${translation('manageSeats.allowed_range', {
                defaultValue: 'allowed'
              })}: ${minSeats}–${maxSeats}`
            : ` — ${translation('manageSeats.minimum', {
                defaultValue: 'minimum'
              })}: ${minSeats}`}
        </p>

        {validationReason === 'below-min' ? (
          <p className="p-invalid mb-2">
            {translation('manageSeats.below_min', {
              defaultValue: `The number of seats cannot be lower than ${minSeats}.`
            })}
          </p>
        ) : null}
        {validationReason === 'above-max' && maxSeats != null ? (
          <p className="p-invalid mb-2">
            {translation('manageSeats.above_max', {
              defaultValue: `The number of seats cannot be higher than ${maxSeats}.`
            })}
          </p>
        ) : null}

        {/* Live pro-ration preview */}
        {!isUnchanged && validationReason === null ? (
          <div className="alert-message p-3 mt-2" role="status">
            {isPreviewingSeatChange || !preview ? (
              <span>
                {translation('manageSeats.calculating', {
                  defaultValue: 'Calculating pro-rated amount…'
                })}
              </span>
            ) : (
              <>
                <Row className="mb-1">
                  <Col>
                    {translation('manageSeats.new_price', {
                      defaultValue: 'New price per billing cycle'
                    })}
                    :
                  </Col>
                  <Col className="text-right font-weight-bold">
                    {currencyFormatter(Number(preview.newPrice))}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {isUpgrade
                      ? translation('manageSeats.prorated_charge', {
                          defaultValue: 'Pro-rated charge now'
                        })
                      : translation('manageSeats.prorated_refund', {
                          defaultValue: 'Pro-rated refund now'
                        })}
                    :
                  </Col>
                  <Col
                    className={`text-right font-weight-bold ${
                      isUpgrade ? 'text-danger' : 'text-success'
                    }`}
                  >
                    {isUpgrade ? '' : '-'}
                    {currencyFormatter(Number(preview.proratedAmount))}
                  </Col>
                </Row>
              </>
            )}
          </div>
        ) : null}
      </Col>
    </DialogWrapper>
  );
};

export default ManageSeatsForm;
